import React, { useState, useEffect } from "react";
import { FiZap, FiGift, FiStar, FiLayers, FiGrid, FiUserCheck, FiSliders, FiHelpCircle, FiCopy, FiSearch } from "react-icons/fi";
import { useBuilder } from "./BuilderContext";
import { Field, TextInput, Card, Chip, cx, Btn } from "./ui";
import { TEMPLATES } from "./templates";
import ModalComponent from "../../../../components/Tenant/ModalComponent/ModalComponent";
import { useGetRulesMutation } from "../../../../features/tenant/rules/rulesApi";

const errText = (errors, path) => errors.find((e) => e.path === path)?.message;

const GROUP_ICONS = {
  Scans: FiGrid,
  Users: FiUserCheck,
  System: FiSliders,
};

export default function StepTrigger({ draft, errors, onChange, onEventType, onTemplate, onCloneRule }) {
  const { meta, token, tenantId } = useBuilder();
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSearch, setCloneSearch] = useState("");
  const [fetchRules, { data: existingRulesRes, isLoading: rulesLoading }] = useGetRulesMutation();

  useEffect(() => {
    if (showCloneModal) {
      fetchRules({
        limit: 50,
        offset: 0,
        token,
        tenant_id: tenantId,
      });
    }
  }, [showCloneModal, fetchRules, token, tenantId]);

  const groups = {};
  (meta?.event_types || []).forEach((e) => { (groups[e.group] = groups[e.group] || []).push(e); });

  const existingRules = (existingRulesRes?.body || []).filter((r) =>
    (r.name || "").toLowerCase().includes(cloneSearch.toLowerCase()) ||
    (r.event_type || "").toLowerCase().includes(cloneSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Quick Start Banners: Templates & Existing Rule Duplication */}
      <div className="grid gap-4 md:grid-cols-[1fr_auto]">
        {onTemplate && (
          <div className="rounded-2xl bg-gradient-to-r from-blue-50/80 via-indigo-50/40 to-blue-50/80 border border-blue-100 p-4 shadow-2xs">
            <div className="flex items-center justify-between gap-2 mb-2.5">
              <div className="flex items-center gap-2">
                <FiZap className="h-4 w-4 text-blue-600 shrink-0" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-blue-900">Start from a Pre-built Template</h4>
              </div>

              {onCloneRule && (
                <button
                  type="button"
                  onClick={() => setShowCloneModal(true)}
                  className="px-3 py-1 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-2xs transition-colors flex items-center gap-1.5 shrink-0"
                >
                  <FiCopy className="h-3.5 w-3.5" />
                  <span>Clone Existing Rule</span>
                </button>
              )}
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onTemplate(t)}
                  className="text-left p-3 rounded-xl bg-white border border-blue-200/80 hover:border-blue-500 hover:shadow-md transition-all group duration-150"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-blue-900 group-hover:text-blue-600 transition-colors">{t.title}</span>
                    <FiGift className="h-3.5 w-3.5 text-blue-400 group-hover:text-blue-600 shrink-0" />
                  </div>
                  <span className="block text-[11px] text-gray-500 line-clamp-2 leading-relaxed">{t.blurb}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rule Identity Card */}
      <div className="grid gap-4 md:grid-cols-2 bg-gray-50/50 rounded-2xl border border-gray-200/80 p-4">
        <Field label="Rule Name" error={errText(errors, "name")} hint="Recognizable title for ledger & admin list">
          <TextInput
            value={draft.name}
            onChange={(e) => onChange({ name: e.target.value })}
            error={errText(errors, "name")}
            placeholder="e.g. First-Scan Welcome Bonus"
          />
        </Field>
        <Field label="Description (optional)">
          <textarea
            rows={2}
            value={draft.description || ""}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Brief explanation of when and why this rule triggers..."
            className="w-full rounded-lg border border-gray-300 px-3.5 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white shadow-2xs"
          />
        </Field>
      </div>

      {/* Trigger Event Selection */}
      <div>
        <div className="mb-3">
          <h3 className="text-sm font-bold text-gray-900">Select Trigger Event</h3>
          <p className="text-xs text-gray-500">Pick the core system event that initiates rule execution and unlocks relevant facts & metrics.</p>
        </div>

        {errText(errors, "event_type") && (
          <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 p-2.5 text-xs text-rose-700 font-medium flex items-center gap-1.5">
            <span>{errText(errors, "event_type")}</span>
          </div>
        )}

        <div className="space-y-5">
          {Object.entries(groups).map(([group, evs]) => {
            const GroupIcon = GROUP_ICONS[group] || FiLayers;
            return (
              <div key={group} className="space-y-2">
                <div className="flex items-center gap-1.5 border-b border-gray-100 pb-1">
                  <GroupIcon className="h-3.5 w-3.5 text-gray-400" />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{group}</p>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {evs.map((e) => {
                    const selected = draft.event_type === e.name;
                    return (
                      <button key={e.name} type="button" onClick={() => onEventType(e.name)} className="text-left">
                        <Card active={selected} className="p-4 h-full cursor-pointer">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-bold text-sm text-gray-900">{e.label}</span>
                            {e.scheme_scoped ? (
                              <Chip color="blue">scheme scoped</Chip>
                            ) : (
                              <Chip color="gray">global</Chip>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed mb-2">{e.description}</p>
                          {e.example && (
                            <div className="flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-100 font-mono">
                              <span className="text-gray-400 font-sans italic">e.g.</span> {e.example}
                            </div>
                          )}
                        </Card>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Clone Existing Rule Modal */}
      <ModalComponent size="lg" show={showCloneModal} setShow={setShowCloneModal} showClose={true}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Clone Existing Rule</h3>
              <p className="text-xs text-gray-500">Pick any existing active or past rule to pre-populate a new wizard draft.</p>
            </div>
          </div>

          <div className="mb-4 relative">
            <input
              type="text"
              placeholder="Search by rule name or trigger event..."
              value={cloneSearch}
              onChange={(e) => setCloneSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
            <FiSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>

          {rulesLoading ? (
            <div className="text-center py-10 text-gray-500 flex flex-col items-center gap-2">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
              <span className="text-xs font-medium">Fetching existing rules...</span>
            </div>
          ) : existingRules.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              No matching rules found.
            </div>
          ) : (
            <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
              {existingRules.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between border border-gray-200/80 rounded-xl p-3.5 hover:border-blue-400 hover:bg-blue-50/20 transition-all shadow-2xs"
                >
                  <div>
                    <div className="font-bold text-sm text-gray-900">{r.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                      <span className="font-mono text-[11px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{r.event_type}</span>
                      <span>•</span>
                      <span className="capitalize">{r.reward_mode || "one_time"}</span>
                    </div>
                  </div>
                  <Btn
                    variant="soft"
                    onClick={() => {
                      if (onCloneRule) onCloneRule(r);
                      setShowCloneModal(false);
                    }}
                    className="text-xs"
                  >
                    <FiCopy className="h-3.5 w-3.5" /> Clone as Draft
                  </Btn>
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalComponent>
    </div>
  );
}


